import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@api/api.service';
import { Filesystem } from '@api/types';
import { RavIcon, RavIconOutlined } from '@shared/icon/icon';
import { EditorService } from '@shared/services/editor.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { MobileService } from '@shared/services/mobile.service';
import { ScopeService } from '@shared/services/scope.service';
import { SidebarService } from '@shared/services/sidebar.service';
import { Scope } from '@shared/types/scope';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
@Component({
  selector: 'rav-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss']
})
export class EditorComponent implements OnInit, OnDestroy {
  /* We are only updating the cached value when the user submit the edit,
  otherwise we will get a strange behavior. */
  @Input() set currentText(value: string) {
    this._currentText = value;
    this.validate();
  }
  get currentText(): string {
    return this._currentText;
  }
  @Input() showIconOnHover = true;
  @Input() textAppended = '';
  @Input() editType: string;
  @Input() id: string;
  @Input() isLink = false;
  @Input() link: string;
  @Input() disabled: boolean;
  @Input() isMobileTable = false;
  @Input() editorClass = '';
  @Output() editionEvent = new EventEmitter<any>();

  editable = false;
  isMouseDown = false;
  RavIconOutlined = RavIconOutlined;
  RavIcon = RavIcon;
  savedText: string;
  shouldShowIcon = false;

  isValid: boolean;
  private subscriptions: Subscription[] = [];
  private _currentText: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private snackbar: MatSnackBar,
    private sidebarService: SidebarService,
    private scope: ScopeService,
    private mobileService: MobileService,
    private editorService: EditorService,
    private localStorage: LocalStorageService
  ) {}

  /**
   * Catch all click events, if outside of the component then stop the editor
   *
   * @param event all click events
   */
  @HostListener('document:click', ['$event'])
  clickOut(event: any) {
    if (event && event.target && event.target.innerText && event.target.innerText.toLowerCase().includes('rename')) {
      return;
    }
    if (this.isMouseDown && this.editable) {
      return;
    }
    if (this.savedText && this.savedText.length > 0) {
      this.currentText = this.savedText;
    }
  }

  ngOnInit() {
    if (this.currentText && this.currentText.length > 0) {
      this.savedText = this.currentText;
    }
    if (!this.showIconOnHover) {
      this.shouldShowIcon = true;
    }
    this.subscriptions.push(
      this.editorService.editorState$.subscribe(({ id, open, event }) => {
        // on Dragging action, do nothing
        if (event && event.type === 'click' && this.isMouseDown) {
          return;
        }
        // handles openEditor for -right click + 'Rename'- on a table element
        if (id === this.id && open) {
          this.savedText = this.currentText;
          this.editable = true;
        } else {
          this.editable = false;
          // hides icon to be shown only onHover action
          if (this.showIconOnHover) {
            this.shouldShowIcon = false;
          }
        }
      })
    );
    if (!this.editType) {
      this.fetchFolderInfoFromRoute();
      this.subscriptions.push(
        this.router.events.subscribe(() => {
          this.fetchFolderInfoFromRoute();
        })
      );
    }
  }

  /**
   *  If current file type or id does not exist then we fetch from the url, only if the url contains folders and id
   */
  fetchFolderInfoFromRoute() {
    if (this.router.url.includes('folders') && this.activatedRoute.snapshot.params['folderId']) {
      this.editType = 'folder';
      this.id = this.activatedRoute.snapshot.params['folderId'];
    }
  }

  validate() {
    this.isValid = this.currentText && this.currentText.trim().length >= 2;
  }

  /**
   *  Unsubscribe all subscriptions
   */
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   *  Mouse move in should show rename icon except for inbox and folders
   */
  onMouseEnter() {
    if (this.disabled || !this.showIconOnHover) return;

    this.shouldShowIcon = false;
    if (
      !this.mobileService.isMobileDisplay() &&
      ((this.editType === 'document' && this.scope.hasAccess(Scope.DocumentWrite)) ||
        (this.editType === 'folder' && this.scope.hasAccess(Scope.FolderWrite)))
    ) {
      this.shouldShowIcon = true;
    }
  }

  /**
   * Mouse move out should remove the rename icon.
   */
  onMouseLeave() {
    if (this.showIconOnHover) {
      this.shouldShowIcon = false;
    }
  }

  /**
   * Save the edited text
   *
   * @param event mouse event
   */
  async editText(event: Event) {
    if (this.savedText !== this.currentText) {
      this.stopParentAction(event);
      switch (this.editType) {
        case 'folder':
          this.patchFolder(this.currentText);
          break;
        case 'document':
          this.patchFile(this.currentText);
          break;
        case 'ravenEmail':
          this.patchInboundEmail(this.currentText);
          break;
        default:
          break;
      }
    } else {
      this.cancel(event);
    }
  }

  /**
   * Cancel editing
   *
   * @param event mouse event
   */
  cancel(event: Event) {
    this.stopParentAction(event);
    this.editable = false;
    this.closeEditor();
  }

  /**
   * Display the input text field when the user clicks rename icon
   *
   */
  showEditor() {
    this.editorService.openOrCloseEditor(this.id, true, null);
  }

  /**
   * Close the input text field.
   *
   */
  closeEditor() {
    this.editorService.openOrCloseEditor(null, false, null);
  }

  /**
   * Cancel the parent component action
   *
   * @param event mouse event
   */
  stopParentAction(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * Navigate to the document view
   */
  navigateToLink() {
    if (this.disabled) return;
    this.closeEditor();
    this.router.navigate([this.link]);
  }

  /**
   * Mouse right click events
   *
   * @param event Mouse click event
   */
  onRightClick(event) {
    if (this.editable) {
      event.stopPropagation();
    }
  }

  /**
   * Handle when drag mouse outside of the editor
   *
   * @param isMouseDown Is mouse press down
   */
  setMouseDown(isMouseDown: boolean, event: Event) {
    event.stopPropagation();
    this.isMouseDown = isMouseDown;
  }

  private patchFolder(name: string) {
    this.subscriptions.push(
      this.api.folders.byId
        .patch(this.id, { title: name })
        .pipe(
          finalize(() => {
            this.closeEditor();
          })
        )
        .subscribe(
          (folder) => {
            this.currentText = folder.title;
            this.savedText = '';
            this.sidebarService.renameFolder.emit(folder);
            if (this.router.url.includes('/search')) this.updateFileNameInStorageLastSearchResults(name);
            this.displayMessage(true, 'The folder renamed successfully');
            this.emitRenameEvent();
          },
          () => {
            this.currentText = this.savedText;
            this.displayMessage(false, 'Failed to renamed the folder');
          }
        )
    );
  }

  private patchFile(name: string) {
    this.subscriptions.push(
      this.api.documents.byId
        .patch(this.id, { title: name })
        .pipe(
          finalize(() => {
            this.closeEditor();
          })
        )
        .subscribe(
          () => {
            this.savedText = '';
            if (this.router.url.includes('/search')) this.updateFileNameInStorageLastSearchResults(name);
            this.displayMessage(true, 'The document renamed successfully');
            this.emitRenameEvent();
          },
          () => {
            this.currentText = this.savedText;
            this.displayMessage(false, 'Failed to rename the document');
          }
        )
    );
  }

  private patchInboundEmail(currentText: string) {
    const newEmail = `${currentText}${this.textAppended}`;
    this.subscriptions.push(
      this.api.accounts.byId
        .patch(this.localStorage.userWithToken.account_id, { incoming_email: newEmail })
        .pipe(
          finalize(() => {
            this.closeEditor();
          })
        )
        .subscribe(
          () => {
            this.savedText = '';
            this.displayMessage(true, 'The email renamed successfully');
            this.emitRenameEvent();
          },
          () => {
            this.currentText = this.savedText;
            this.displayMessage(false, 'Failed to rename email. Please try a different name');
          }
        )
    );
  }

  private updateFileNameInStorageLastSearchResults(name: string) {
    const lastSearch = this.localStorage.lastSearch;
    if (!lastSearch || !lastSearch.searchResults || !lastSearch.searchResults.data) return;
    const renamedFile: Filesystem = lastSearch.searchResults.data.find((file: Filesystem) => file.id === this.id);
    if (renamedFile) {
      renamedFile.title = name;
      renamedFile.updated_at = new Date().toISOString();
      this.localStorage.lastSearch = lastSearch;
    }
  }

  /**
   * Display the message by snackbar
   *
   * @param isSuccess Succeed or not
   * @param message Message to be displayed
   */
  private displayMessage(isSuccess: boolean, message?: string) {
    this.snackbar.open(message, '', {
      duration: 3000,
      panelClass: [isSuccess ? 'success-bg' : 'alert-bg', 'bold', 'light-color']
    });
  }

  /**
   * Emits a file rename event with the new name and id of the row. Also closes the editor.
   */
  private emitRenameEvent() {
    this.editionEvent.emit({
      text: this.currentText + this.textAppended,
      id: this.id,
      updated: new Date()
    });
  }
}
